import {Point, util} from '@postermywall/fabricjs-2';
import type {TMat2D} from '@postermywall/fabricjs-2';
import {degreesToRadians, radiansToDegrees} from '@Utils/math.util';
import type {CropFrame, StraightenCropState} from '@PosterWhiteboard/poster/poster-media-crop/poster-media-crop.types';

const CONTAINMENT_EPSILON = 1e-6;

/**
 * Visual source space -> scene: place the window on the canvas, then spin the picture about the
 * window's centre, then bring the source point under that centre to the origin.
 */
export const getPictureMatrix = (state: StraightenCropState): TMat2D => {
  return util.multiplyTransformMatrixArray([
    util.composeMatrix({
      translateX: state.windowCentreScene.x,
      translateY: state.windowCentreScene.y,
      angle: state.windowAngle,
      scaleX: state.scaleX,
      scaleY: state.scaleY,
    }),
    util.composeMatrix({angle: state.pictureRotation}),
    util.createTranslateMatrix(-state.windowCentre.x, -state.windowCentre.y),
  ]);
};

export const sourceToScene = (state: StraightenCropState, sourcePoint: Point): Point => {
  return sourcePoint.transform(getPictureMatrix(state));
};

export const sceneToSource = (state: StraightenCropState, scenePoint: Point): Point => {
  return scenePoint.transform(util.invertTransform(getPictureMatrix(state)));
};

/** A scene-space displacement expressed in visual source pixels: the picture's rotation and scale undone. */
export const sceneVectorToSource = (state: StraightenCropState, sceneVector: Point): Point => {
  const alongWindow = sceneVector.rotate(-degreesToRadians(state.windowAngle));
  return new Point(alongWindow.x / state.scaleX, alongWindow.y / state.scaleY).rotate(-degreesToRadians(state.pictureRotation));
};

/** A scene-space point in the window's own frame: origin at the window centre, axes along its edges. */
export const sceneToWindowLocal = (state: StraightenCropState, scenePoint: Point): Point => {
  return scenePoint.subtract(state.windowCentreScene).rotate(-degreesToRadians(state.windowAngle));
};

export const windowLocalToScene = (state: StraightenCropState, localPoint: Point): Point => {
  return state.windowCentreScene.add(localPoint.rotate(degreesToRadians(state.windowAngle)));
};

/**
 * Half the width and height of the window's bounding box in source space, once the picture's
 * rotation is taken into account. The window fits the picture exactly when its centre stays at least
 * this far from every edge.
 */
export const getWindowHalfExtentsInSource = (state: StraightenCropState): Point => {
  const rotation = degreesToRadians(state.pictureRotation);
  const cos = Math.abs(Math.cos(rotation));
  const sin = Math.abs(Math.sin(rotation));
  return new Point((state.windowWidth / 2) * cos + (state.windowHeight / 2) * sin, (state.windowWidth / 2) * sin + (state.windowHeight / 2) * cos);
};

export const isWindowInsideSource = (state: StraightenCropState): boolean => {
  const extents = getWindowHalfExtentsInSource(state);
  const centre = state.windowCentre;
  return (
    centre.x >= extents.x - CONTAINMENT_EPSILON &&
    centre.x <= state.sourceWidth - extents.x + CONTAINMENT_EPSILON &&
    centre.y >= extents.y - CONTAINMENT_EPSILON &&
    centre.y <= state.sourceHeight - extents.y + CONTAINMENT_EPSILON
  );
};

/** The nearest window centre at which the (possibly rotated) window still lies within the picture. */
export const clampWindowCentre = (state: StraightenCropState): Point => {
  const extents = getWindowHalfExtentsInSource(state);
  return new Point(clampToBand(state.windowCentre.x, extents.x, state.sourceWidth), clampToBand(state.windowCentre.y, extents.y, state.sourceHeight));
};

/**
 * Keeps the window inside the picture after a rotation without moving the window on screen: the
 * window shrinks in source pixels (the picture zooms in) just enough for a centred window to fit,
 * then its centre is nudged back inside the picture if it had drifted past an edge.
 */
export const fitWindowInsideSource = (state: StraightenCropState): StraightenCropState => {
  const extents = getWindowHalfExtentsInSource(state);
  const shrink = Math.min(1, state.sourceWidth / 2 / extents.x, state.sourceHeight / 2 / extents.y);
  const shrunk: StraightenCropState = {
    ...state,
    windowWidth: state.windowWidth * shrink,
    windowHeight: state.windowHeight * shrink,
    scaleX: state.scaleX / shrink,
    scaleY: state.scaleY / shrink,
  };
  return {...shrunk, windowCentre: clampWindowCentre(shrunk)};
};

/** The crop window as an on-screen box: its rotated top-left, size and angle in scene units. */
export const getWindowFrame = (state: StraightenCropState): CropFrame => {
  const width = state.windowWidth * state.scaleX;
  const height = state.windowHeight * state.scaleY;
  const topLeft = windowLocalToScene(state, new Point(-width / 2, -height / 2));
  return {left: topLeft.x, top: topLeft.y, width, height, angle: state.windowAngle};
};

/**
 * The full picture as an on-screen box, read off its corners so it holds under flips. A stretched
 * item turned inside its window is a parallelogram; the box then follows its top edge and left edge.
 */
export const getPictureFrame = (state: StraightenCropState): CropFrame => {
  const topLeft = sourceToScene(state, new Point(0, 0));
  const topRight = sourceToScene(state, new Point(state.sourceWidth, 0));
  const bottomLeft = sourceToScene(state, new Point(0, state.sourceHeight));
  const topEdge = topRight.subtract(topLeft);
  return {
    left: topLeft.x,
    top: topLeft.y,
    width: Math.hypot(topEdge.x, topEdge.y),
    height: bottomLeft.distanceFrom(topLeft),
    angle: radiansToDegrees(Math.atan2(topEdge.y, topEdge.x)),
  };
};

const clampToBand = (value: number, halfExtent: number, size: number): number => {
  if (halfExtent * 2 > size) {
    return size / 2;
  }
  return Math.min(Math.max(value, halfExtent), size - halfExtent);
};
